import { CONSOLE_PATHS, EMAIL_MAX_LENGTH } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Input, Label, cn } from "@sagot/ui";
import { useState, type SyntheticEvent } from "react";
import { OAUTH_PROVIDER_LIVE, WORK_EMAIL_PLACEHOLDER } from "./auth-constants";
import { AuthDivider } from "./components/auth-divider";
import { MicrosoftMark } from "./components/brand-icons";
import { OAuthButton } from "./components/oauth-buttons";
import { REGISTER_PLAN_NAMES, type RegisterPlanKey } from "./register-constants";
import { CheckGlyph } from "./register-icons";
import { PaneTitle, RegisterPane } from "./register-pane";
import { isEmail } from "./register-rules";

const HELP_ID = "r-email-help";

interface RegisterEmailStepProps {
  email: string;
  onEmailChange: (email: string) => void;
  plan: RegisterPlanKey | null;
  back: boolean;
  onNext: () => void;
}

/** Step 1 (mockup pane 1): work email, plan badge, Google and Microsoft. Nothing is sent yet: the e-mail is checked for its shape. */
export function RegisterEmailStep({ email, onEmailChange, plan, back, onNext }: RegisterEmailStepProps) {
  const { t } = useTranslation();
  const [bad, setBad] = useState(false);
  const badText = email.trim().length > EMAIL_MAX_LENGTH ? t("auth.common.emailTooLong") : t("auth.register.emailBad");

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (!isEmail(email)) {
      setBad(true);
      return;
    }
    setBad(false);
    onNext();
  };

  return (
    <RegisterPane back={back} onSubmit={submit}>
      {plan ? (
        <div className="r-badge" data-testid="plan-badge">
          <span>
            <CheckGlyph />
            {t("auth.register.planBadgeLead")} <b>{REGISTER_PLAN_NAMES[plan]}</b> {t("auth.register.planBadgeTail")}
          </span>
        </div>
      ) : null}
      <PaneTitle>{t("auth.register.trialHeading")}</PaneTitle>
      <p className="a-sub">{t("auth.register.trialSub")}</p>
      <div className="a-fld">
        <Label htmlFor="r-email">{t("auth.common.workEmail")}</Label>
        <Input
          id="r-email"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder={WORK_EMAIL_PLACEHOLDER}
          value={email}
          invalid={bad || undefined}
          aria-describedby={HELP_ID}
          onChange={(event) => {
            onEmailChange(event.target.value);
            if (bad && isEmail(event.target.value)) setBad(false);
          }}
        />
        <p id={HELP_ID} className={cn("hint", bad && "hint-err")} role={bad ? "alert" : undefined}>
          {bad ? badText : t("auth.register.emailHelp")}
        </p>
      </div>
      <Button type="submit" size="lg" className="w-full">
        {t("auth.register.continue")}
      </Button>
      <AuthDivider label={t("auth.register.orSignUpWith")} />
      <div className="r-2">
        <OAuthButton provider="google" intent="signup" label={t("auth.common.google")} />
        {OAUTH_PROVIDER_LIVE.microsoft ? (
          <OAuthButton provider="microsoft" intent="signup" label={t("auth.common.microsoft")} />
        ) : (
          <Button type="button" variant="secondary" size="lg" disabled title={t("auth.common.notConfigured")}>
            <MicrosoftMark />
            {t("auth.common.microsoft")}
          </Button>
        )}
      </div>
      <p className="a-foot">
        {t("auth.common.alreadyHaveAccount")}{" "}
        <AppLink href={CONSOLE_PATHS.login} className="a-link">
          {t("auth.common.signIn")}
        </AppLink>
      </p>
    </RegisterPane>
  );
}
